import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { PAPER_COPY_NOTE_MAX, PAPER_COPY_REQUEST_INTERVAL_MS } from '@lib/constants/envelope'
import { paperCopyFeeSentence } from '@lib/helpers/consent'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'

interface PaperCopyRequestProps {
  organization: string
  feeMicros: number
  /** When the signer last asked; another request isn't offered for a day. */
  requestedAt: string | null
  /** The signer can still sign here, so the dialog says a request doesn't change that. */
  stillSigning: boolean
  requesting: boolean
  error: string | null
  onRequest: (note: string) => void
  /** For tests: the current time. */
  now?: () => number
}

/**
 * Asking the organization for a paper copy, as the consent promises
 * (docs/consent-and-paper-copies.md). The organization is told and mails it;
 * PixelSign doesn't. Once asked, it says so for a day instead of offering
 * another request.
 */
export function PaperCopyRequest({
  organization,
  feeMicros,
  requestedAt,
  stillSigning,
  requesting,
  error,
  onRequest,
  now = Date.now,
}: PaperCopyRequestProps) {
  const [open, setOpen] = React.useState(false)
  const [note, setNote] = React.useState('')
  // A double click lands both clicks before `requesting` turns the button off.
  const sentRef = React.useRef(false)

  React.useEffect(() => {
    if (!requesting) {
      sentRef.current = false
    }
  }, [requesting, open])

  function send(): void {
    if (sentRef.current) {
      return
    }
    sentRef.current = true
    onRequest(note.trim())
  }

  const askedAt = requestedAt ? Date.parse(requestedAt) : null
  if (askedAt !== null && now() - askedAt < PAPER_COPY_REQUEST_INTERVAL_MS) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        You asked {organization} for a paper copy on {new Date(askedAt).toLocaleString()}. They’ll mail it to you.
      </p>
    )
  }

  return (
    <>
      <p className="text-sm text-muted-foreground">
        Want these documents on paper?{' '}
        <Button type="button" variant="link" size="sm" onClick={() => setOpen(true)}>
          Request a paper copy
        </Button>
      </p>
      <Dialog open={open} onOpenChange={(next) => !requesting && setOpen(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request a paper copy</DialogTitle>
            <DialogDescription>
              {organization} is told and mails you a paper copy of these documents.{' '}
              {paperCopyFeeSentence(organization, feeMicros)}
              {stillSigning ? ' Asking doesn’t change anything here: you can still sign or decline.' : null}
            </DialogDescription>
          </DialogHeader>
          <Field label={`Note for ${organization} (optional)`} htmlFor="paper-copy-note">
            <Textarea
              id="paper-copy-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="For example, the address to mail it to"
              rows={3}
              maxLength={PAPER_COPY_NOTE_MAX}
            />
          </Field>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={requesting} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={requesting} onClick={send}>
              {requesting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
